import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import DataTable, { type DataTableColumn } from '@shared/components/DataTable';
import { useDownloadArtifact } from '../hooks/useDownloadArtifact';
import type { UpdateOperation } from '../types';
import type { OperationStatus } from '@shared/types';

const statusColorMap: Record<
  OperationStatus,
  'default' | 'info' | 'success' | 'error' | 'warning'
> = {
  queued: 'default',
  running: 'info',
  succeeded: 'success',
  failed: 'error',
  partial: 'warning',
  cancelled: 'warning',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function copyToClipboard(text: string) {
  void navigator.clipboard.writeText(text);
}

interface Props {
  operations: UpdateOperation[];
  loading?: boolean;
}

export default function OperationsTable({ operations, loading = false }: Props) {
  const downloadArtifact = useDownloadArtifact();

  const columns: DataTableColumn<UpdateOperation>[] = [
    {
      id: 'operationId',
      label: 'Operation',
      width: 130,
      render: (row) => row.operationId,
    },
    {
      id: 'correlationId',
      label: 'Correlation',
      width: 140,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <span>{row.correlationId}</span>
          <Tooltip title="Copy">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(row.correlationId);
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      width: 90,
      render: (row) => row.type,
    },
    {
      id: 'status',
      label: 'Status',
      width: 100,
      render: (row) => <Chip label={row.status} color={statusColorMap[row.status]} size="small" />,
    },
    {
      id: 'progress',
      label: 'Progress',
      width: 120,
      render: (row) => {
        if (row.status === 'running' || row.status === 'queued') {
          return (
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                variant={row.status === 'queued' ? 'indeterminate' : 'determinate'}
                value={row.progressPercent}
              />
            </Box>
          );
        }
        return `${row.progressPercent}%`;
      },
    },
    {
      id: 'started',
      label: 'Started',
      width: 100,
      render: (row) => formatDate(row.startedAtUtc),
    },
    {
      id: 'finished',
      label: 'Finished',
      width: 100,
      render: (row) => formatDate(row.finishedAtUtc),
    },
    {
      id: 'actions',
      label: '',
      width: 80,
      render: (row) =>
        row.status === 'succeeded' && row.artifactId ? (
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              void downloadArtifact(row.artifactId!);
            }}
            sx={{ textTransform: 'none', minWidth: 0 }}
          >
            Download
          </Button>
        ) : null,
    },
  ];

  return (
    <div data-testid="operations-table">
      <DataTable
        columns={columns}
        rows={operations}
        rowKey={(row) => row.operationId}
        loading={loading}
        emptyMessage="No operations found"
      />
    </div>
  );
}
